import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";

import apiClient from "../../axiosInstance";
import showAlert from "../../components/Common/ShowAlert";
import TanstackTable from "../../components/Common/TanstackTable";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import logo from "../../assets/images/logo.png";

const getTodayDate = () => new Date().toISOString().split("T")[0];
const toInputDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const Payments = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Payments", link: "#" },
  ]);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({ from_date: "", to_date: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Options
  const [unpaidCharges, setUnpaidCharges] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [filteredPaymentAccounts, setFilteredPaymentAccounts] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    paymentID: null,
    chargeId: "",
    paymentMethodId: "",
    paymentAccountId: "",
    paymentReference: "",
    amount: "",
    paymentDate: getTodayDate(),
  });

  // Fetch payments list
  const fetchPayments = async () => {
    try {
      const result = await apiClient.get("/payments/payments");
      setData(result.data.response || []);
    } catch (err) {
      console.error("Error fetching payments:", err);
    }
  };

  // Fetch options for modal
  const fetchOptions = async () => {
    try {
      // Unpaid charges
      const chargesRes = await apiClient.get("/payments/charges");
      const charges = chargesRes.data.response || [];
      setUnpaidCharges(
        charges
          //   .filter((c) => c.status === "Unpaid")
          .map((c) => ({
            value: c.id,
            label: `${c.make_name || ""} ${c.model_name || ""} (${
              c.license_plate || ""
            })`,
            registration_id: c.registration_id,
            amount: c.amount,
            make_name: c.make_name,
            model_name: c.model_name,
            license_plate: c.license_plate,
            full_name: c.full_name,
            phone_number: c.phone_number,
            registration_date: c.registration_date,
            purpose: c.purpose,
          }))
      );

      // Payment methods
      const methodsRes = await apiClient.get("/payment/payment-methods");
      const methods = methodsRes.data.response || methodsRes.data || [];
      setPaymentMethods([
        { label: "Select Payment Method", value: "" },
        ...methods.map((m) => ({ value: m.id, label: m.name })),
      ]);

      // Payment accounts
      const accountsRes = await apiClient.get("/payment/payment-accounts");
      const accounts = accountsRes.data.response || accountsRes.data || [];
      setPaymentAccounts([
        { label: "Select Payment Account", value: "" },
        ...accounts.map((a) => ({
          value: a.id,
          label: a.account,
          methodId: a.method_id,
        })),
      ]);
    } catch (err) {
      console.error("Error fetching options:", err);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchOptions();
  }, []);

  const getFilteredPayments = (rows, activeFilters) => {
    const fromDate = activeFilters.from_date
      ? new Date(`${activeFilters.from_date}T00:00:00`)
      : null;
    const toDate = activeFilters.to_date
      ? new Date(`${activeFilters.to_date}T23:59:59`)
      : null;

    return rows.filter((row) => {
      const rowDateValue = row.paid_at || row.registration_date;
      if (!rowDateValue) {
        return !fromDate && !toDate;
      }

      const rowDate = new Date(rowDateValue);
      if (Number.isNaN(rowDate.getTime())) {
        return !fromDate && !toDate;
      }

      if (fromDate && rowDate < fromDate) return false;
      if (toDate && rowDate > toDate) return false;
      return true;
    });
  };

  useEffect(() => {
    setFilteredData(getFilteredPayments(data, filters));
  }, [data]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = async () => {
    if (
      filters.from_date &&
      filters.to_date &&
      filters.from_date > filters.to_date
    ) {
      await showAlert({
        title: "Payments",
        text: "From date cannot be greater than To date",
        icon: "warning",
      });
      return;
    }

    setFilteredData(getFilteredPayments(data, filters));
  };

  const resetFilters = () => {
    const resetState = { from_date: "", to_date: "" };
    setFilters(resetState);
    setFilteredData(data);
  };

  const formatDate = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString();
  };

  const handlePrint = async () => {
    if (!filteredData.length) {
      await showAlert({
        title: "Payments",
        text: "No data available to print",
        icon: "warning",
      });
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const reportRows = filteredData
      .map(
        (row, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(`${row.make_name || ""} ${row.model_name || ""}`.trim())}</td>
            <td>${escapeHtml(row.license_plate || "")}</td>
            <td>${escapeHtml(row.full_name || "")}</td>
            <td>${escapeHtml(formatDate(row.paid_at))}</td>
            <td>${escapeHtml(row.amount_paid || "")}</td>
            <td>${escapeHtml(row.account_name || "")}</td>
            <td>${escapeHtml(row.payment_reference || "")}</td>
          </tr>
        `
      )
      .join("");

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      await showAlert({
        title: "Payments",
        text: "Unable to open print window. Please allow popups and try again.",
        icon: "error",
      });
      return;
    }

    const generatedAt = new Date().toLocaleString();
    printWindow.document.write(`
      <html>
        <head>
          <title>Payments Report</title>
          <style>
            @page { margin: 15mm 14mm; }
            body { font-family: "Segoe UI", Arial, sans-serif; margin: 18px; color: #1f2937; }
            .header { text-align: center; border-bottom: 2px solid #6b7280; padding-bottom: 12px; margin-bottom: 12px; }
            .logo { width: 100mm; height: 50mm; object-fit: contain; padding-top: 10mm; margin-bottom: 6px; margin-top: -50px; }
            h2 { margin: 0 0 6px; font-weight: 700; }
            .meta { margin-bottom: 14px; font-size: 12px; color: #374151; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 18px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #6b7280; padding: 7px 8px; text-align: left; }
            th { background: #e5e7eb; color: #111827; }
            tbody tr:nth-child(even) { background: #f9fafb; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${logo}" alt="Daryeel Logo" class="logo" />
            <h2>Payments Report</h2>
          </div>
          <div class="meta">
            <div><strong>From Date:</strong> ${escapeHtml(filters.from_date || "-")}</div>
            <div><strong>To Date:</strong> ${escapeHtml(filters.to_date || "-")}</div>
            <div><strong>Generated:</strong> ${escapeHtml(generatedAt)}</div>
            <div><strong>Total Records:</strong> ${filteredData.length}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>Vehicle</th>
                <th>License</th>
                <th>Owner</th>
                <th>Paid Date</th>
                <th>Amount</th>
                <th>Account</th>
                <th>Reference</th>
              </tr>
            </thead>
            <tbody>${reportRows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleDeletePayment = async (paymentId) => {
    const confirmResult = await Swal.fire({
      title: "Delete Payment?",
      text: "Are you sure you want to delete this payment?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      const response = await apiClient.delete(`/payments/payments/${paymentId}`);

      if (response.data.status === "success") {
        setData((prevData) =>
          prevData.filter((payment) => payment.payment_id !== paymentId)
        );
        await Swal.fire({
          title: "Deleted",
          text: response.data.message || "Payment deleted successfully",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        return;
      }

      await Swal.fire({
        title: "Delete Failed",
        text: response.data.message || "Failed to delete payment",
        icon: "error",
      });
    } catch (error) {
      console.error("Error deleting payment:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to delete payment";
      await Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });
    }
  };

  // Table columns
  const columns = [
    {
      id: "index",
      header: "No",
      cell: ({ row }) => row.index + 1,
      enableColumnFilter: false,
    },
    {
      id: "vehicle",
      header: "Vehicle",
      cell: ({ row }) => (
        <div>
          <div>
            {row.original.make_name} {row.original.model_name}
          </div>
          <div className="pt-2">
            {row.original.purpose}{" "}
            <span className="badge bg-info">{row.original.license_plate}</span>
          </div>
        </div>
      ),
      accessorFn: (row) =>
        `${row.make_name || ""} ${row.model_name || ""} ${
          row.license_plate || ""
        } ${row.purpose || ""}`,
      filterFn: "includesString",
      enableColumnFilter: true,
    },
    {
      id: "owner",
      header: "Owner",
      cell: ({ row }) => (
        <div>
          <div>{row.original.full_name}</div>
          <div className="pt-2">
            {row.original.phone_number}
          </div>
        </div>
      ),
      accessorFn: (row) =>
        `${row.full_name || ""} ${row.phone_number || ""}`,
      filterFn: "includesString",
      enableColumnFilter: true,
    },
    {
      accessorKey: "registration_date",
      header: "Reg Date",
      cell: ({ row }) =>
        row.original.registration_date
          ? new Date(row.original.registration_date).toLocaleDateString()
          : "",
      filterFn: "includesString",
      enableColumnFilter: true,
    },
    {
      accessorKey: "branch",
      header: "Branch",
      filterFn: "includesString",
      enableColumnFilter: true,
    },
    {
      accessorKey: "amount_paid",
      header: "Amount",
      filterFn: "includesString",
      enableColumnFilter: true,
    },
    {
      id: "reference",
      header: "Reference",
      cell: ({ row }) => (
        <div>
          <div>{row.original.account_name} </div>
          <div className="pt-2">{row.original.payment_reference}</div>
        </div>
      ),
      accessorFn: (row) =>
        `${row.account_name || ""} ${row.payment_reference || ""}`,
      filterFn: "includesString",
      enableColumnFilter: true,
    },
    {
      accessorKey: "paid_at",
      header: "Paid Date",
      cell: ({ row }) =>
        row.original.paid_at
          ? new Date(row.original.paid_at).toLocaleDateString()
          : "",
      filterFn: "includesString",
      enableColumnFilter: true,
    },
    {
      accessorKey: "paid_by",
      header: "User",
      filterFn: "includesString",
      enableColumnFilter: true,
    },
    {
      id: "print",
      header: "",
      cell: ({ row }) => (
        <button
          className="btn btn-sm btn-success"
          title="Print"
          onClick={() =>
            window.open(
              `https://daryeelinspection.com/api/payments/invoice/${row.original.charge_id}`,
              "_blank"
            )
          }
        >
          <i className="ri-printer-line"></i>
        </button>
      ),
      enableColumnFilter: false,
    },
    {
      id: "edit",
      header: "",
      cell: ({ row }) => (
        <button
          className="btn btn-sm btn-primary"
          title="Edit"
          onClick={() => {
            setIsEditing(true);
            setFormData({
              paymentID: row.original.payment_id || null,
              chargeId: row.original.charge_id || "",
              paymentMethodId: row.original.method_id,
              paymentAccountId: row.original.account_id,
              paymentReference: row.original.payment_reference,
              amount: row.original.amount_paid,
              paymentDate: toInputDate(row.original.paid_at) || getTodayDate(),
            });
            // Filter accounts for the selected method
            setFilteredPaymentAccounts([
              { label: "Select Payment Account", value: "" },
              ...paymentAccounts.filter(
                (a) => a.methodId === row.original.method_id && a.value !== ""
              ),
            ]);
            setModalOpen(true);
          }}
        >
          <i className="ri-file-edit-line"></i>
        </button>
      ),
      enableColumnFilter: false,
    },
    {
      id: "delete",
      header: "",
      cell: ({ row }) => (
        <button
          className="btn btn-sm btn-danger"
          title="Delete"
          onClick={() => handleDeletePayment(row.original.payment_id)}
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      ),
      enableColumnFilter: false,
    },
  ];

  // Modal logic
  const toggleModal = () => {
    setModalOpen(!modalOpen);
    setIsEditing(false);
    setFormData({
      paymentID: null,
      chargeId: "",
      paymentMethodId: "",
      paymentAccountId: "",
      paymentReference: "",
      amount: "",
      paymentDate: getTodayDate(),
    });
    setFilteredPaymentAccounts([]);
  };

  const handleChargeChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      chargeId: selected?.value || "",
      amount: selected?.amount || "",
    }));
  };

  const handleMethodChange = (selected) => {
    const methodId = selected?.value || "";
    setFormData((prev) => ({
      ...prev,
      paymentMethodId: methodId,
      paymentAccountId: "", // Reset account
    }));
    setFilteredPaymentAccounts([
      { label: "Select Payment Account", value: "" },
      ...paymentAccounts.filter(
        (a) => a.methodId === methodId && a.value !== ""
      ),
    ]);
  };

  const handleAccountChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      paymentAccountId: selected?.value || "",
    }));
  };

  const handleReferenceChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      paymentReference: e.target.value,
    }));
  };

  const handlePaymentDateChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      paymentDate: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    try {
      let response;
      if (formData.paymentID) {
        response = await apiClient.put(
          `/payments/payments/${formData.paymentID}`,
          formData
        );
      } else {
        response = await apiClient.post("/payments/payments", formData);
      }
      if (response.data.status === "success") {
        await showAlert({
          title: isEditing ? "Update Payment" : "Payment",
          text: response.data.message,
          icon: response.data.status,
          goBack: false,
          timer: 5000,
          confirm: true,
          link: `https://daryeelinspection.com/api/payments/invoice/${formData.chargeId}`,
        });
        setFormData({
          paymentID: null,
          chargeId: null,
          amount: null,
          paymentMethodId: "",
          paymentAccountId: "",
          paymentReference: "",
          paymentDate: getTodayDate(),
        });
        setModalOpen(false);
        setIsEditing(false);
        fetchPayments();
      }
    } catch (err) {
      console.error("Error submitting payment:", err);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Payments" breadcrumbItems={breadcrumbItems} />
        <div className="mb-3 text-end">
          <button
            className="btn btn-primary"
            onClick={() => {
              setIsEditing(false);
              toggleModal();
            }}
          >
            Receipt Payment
          </button>
        </div>
        <Card>
          <CardBody>
            <Row className="g-3 align-items-end">
              <Col md="4">
                <Label className="form-label fw-semibold">From Date</Label>
                <Input
                  type="date"
                  name="from_date"
                  value={filters.from_date}
                  onChange={handleFilterChange}
                />
              </Col>
              <Col md="4">
                <Label className="form-label fw-semibold">To Date</Label>
                <Input
                  type="date"
                  name="to_date"
                  value={filters.to_date}
                  onChange={handleFilterChange}
                />
              </Col>
              <Col md="4">
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-grow-1"
                    onClick={applyFilters}
                  >
                    Filter
                  </button>
                  <button
                    type="button"
                    className="btn btn-light border flex-grow-1"
                    onClick={resetFilters}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-success flex-grow-1"
                    onClick={handlePrint}
                  >
                    Print
                  </button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <TanstackTable data={filteredData} columns={columns} />
          </CardBody>
        </Card>

        <Modal
          isOpen={modalOpen}
          toggle={toggleModal}
          centered={true}
          backdrop="static"
        >
          <ModalHeader toggle={toggleModal}>
            {isEditing ? "Edit Payment" : "Receipt Payment"}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label>Charge</Label>
              <Select
                value={unpaidCharges.find((c) => c.value === formData.chargeId)}
                onChange={handleChargeChange}
                options={unpaidCharges}
                classNamePrefix="select2-selection form-control"
                placeholder="Select Unpaid Charge"
              />
            </div>
            <div className="mb-3">
              <Label>Amount</Label>
              <input
                type="text"
                className="form-control"
                value={formData.amount || ""}
                onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <Label>Payment Method</Label>
              <Select
                value={paymentMethods.find(
                  (m) => m.value === formData.paymentMethodId
                )}
                onChange={handleMethodChange}
                options={paymentMethods}
                classNamePrefix="select2-selection form-control"
                placeholder="Select Method"
              />
            </div>
            <div className="mb-3">
              <Label>Payment Account</Label>
              <Select
                value={filteredPaymentAccounts.find(
                  (a) => a.value === formData.paymentAccountId
                )}
                onChange={handleAccountChange}
                options={filteredPaymentAccounts}
                classNamePrefix="select2-selection form-control"
                placeholder="Select Account"
                isDisabled={!formData.paymentMethodId}
              />
            </div>
            <div className="mb-3">
              <Label>Reference</Label>
              <input
                type="text"
                className="form-control"
                placeholder="Payment Reference"
                value={formData.paymentReference || ""}
                onChange={handleReferenceChange}
              />
            </div>
            <div className="mb-3">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={formData.paymentDate || ""}
                onChange={handlePaymentDateChange}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-secondary" onClick={toggleModal}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={
                !formData.chargeId ||
                !formData.paymentMethodId ||
                !formData.paymentAccountId ||
                !formData.paymentDate
              }
            >
              {isEditing ? "Update" : "Submit"}
            </button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default Payments;
